import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import socketService from '../services/socketService';

const GlobalMessageContext = createContext({
  // Unread message state
  unreadCount: 0,
  setUnreadCount: () => {},

  // Floating chat widget state
  isFloatingChatOpen: false,
  setIsFloatingChatOpen: () => {},

  // Open chat windows state
  openChats: [],
  addOpenChat: () => {},
  removeOpenChat: () => {},
  minimizedChats: [],
  minimizeChat: () => {},
  maximizeChat: () => {},

  // Recent conversations for floating widget
  recentConversations: [],
  setRecentConversations: () => {},
});

export const useGlobalMessage = () => {
  const context = useContext(GlobalMessageContext);
  if (!context) {
    throw new Error('useGlobalMessage must be used within a GlobalMessageProvider');
  }
  return context;
};

export const GlobalMessageProvider = ({ children }) => {
  // Core state
  const [unreadCount, setUnreadCount] = useState(0);
  const [isFloatingChatOpen, setIsFloatingChatOpen] = useState(false);
  const [openChats, setOpenChats] = useState([]);
  const [minimizedChats, setMinimizedChats] = useState([]);
  const [recentConversations, setRecentConversations] = useState([]);

  // Track processed messages to prevent duplicate unread count increments
  const processedMessageIdsRef = React.useRef(new Set());

  const { user, isAuthenticated } = useAuth();

  // Use centralized socketService instead of creating our own connection
  useEffect(() => {
    if (!isAuthenticated || !user) {
      // Reset state when user logs out
      setUnreadCount(0);
      setRecentConversations([]);
      setOpenChats([]);
      setMinimizedChats([]);
      setIsFloatingChatOpen(false);
      processedMessageIdsRef.current.clear();
      return;
    }

    // Register socket event handlers using centralized socketService
    const handleNewMessage = (data) => {
      console.log('[GlobalMessage] New message received via socketService:', data);

      // Only increment if the message is not from the current user
      if (data.sender?._id !== user._id) {
        // Use message ID or tempId to track if we've already processed this message
        const messageId = data._id || data.tempId || `${data.sender._id}-${data.content}-${data.createdAt}`;

        // Check if we've already processed this message
        if (!processedMessageIdsRef.current.has(messageId)) {
          processedMessageIdsRef.current.add(messageId);

          // Clean up old message IDs to prevent memory leak (keep last 1000)
          if (processedMessageIdsRef.current.size > 1000) {
            const idsArray = Array.from(processedMessageIdsRef.current);
            processedMessageIdsRef.current = new Set(idsArray.slice(-500));
          }

          setUnreadCount(prev => prev + 1);

          // Show browser notification if supported
          showBrowserNotification(data);

          console.log('[GlobalMessage] Incremented unread count for message:', messageId);
        } else {
          console.log('[GlobalMessage] Skipping duplicate message:', messageId);
        }
      }
    };

    const handleMessageRead = (data) => {
      console.log('[GlobalMessage] Messages marked as read:', data);
      setUnreadCount(prev => Math.max(0, prev - (data.readCount || 1)));
    };

    const handleUnreadCountUpdate = (data) => {
      console.log('[GlobalMessage] Unread count update:', data);
      setUnreadCount(data.unreadCount || 0);
    };

    // Register handlers with socketService
    socketService.on('new-message', handleNewMessage);
    socketService.on('message-read', handleMessageRead);
    socketService.on('unread-count-update', handleUnreadCountUpdate);

    console.log('[GlobalMessage] Registered socket event handlers with socketService');

    // Cleanup handlers on unmount
    return () => {
      socketService.off('new-message', handleNewMessage);
      socketService.off('message-read', handleMessageRead);
      socketService.off('unread-count-update', handleUnreadCountUpdate);
      console.log('[GlobalMessage] Unregistered socket event handlers');
    };
  }, [isAuthenticated, user]);

  // Fetch initial unread count when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUnreadCount();
      fetchRecentConversations();
    } else {
      // Reset state when user logs out
      setUnreadCount(0);
      setRecentConversations([]);
      setOpenChats([]);
      setMinimizedChats([]);
      setIsFloatingChatOpen(false);
      processedMessageIdsRef.current.clear(); // Clear processed message tracking
    }
  }, [isAuthenticated, user]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/messages/unread-count', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
      console.error('[GlobalMessage] Error fetching unread count:', error);
    }
  };

  const fetchRecentConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/conversations?limit=10&sort=lastActivity', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecentConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('[GlobalMessage] Error fetching recent conversations:', error);
    }
  };

  // Browser notification function
  const showBrowserNotification = (messageData) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const senderName = messageData.sender ? 
        `${messageData.sender.firstName} ${messageData.sender.lastName}` : 
        'Someone';
      
      new Notification(`New message from ${senderName}`, {
        body: messageData.content ? messageData.content.substring(0, 100) : 'New message',
        icon: '/favicon.ico',
        tag: messageData.threadId,
        requireInteraction: false,
      });
    }
  };

  // Request notification permission
  const requestNotificationPermission = useCallback(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('[GlobalMessage] Notification permission:', permission);
      });
    }
  }, []);

  // Chat window management functions
  const addOpenChat = useCallback((conversation) => {
    const maxOpenChats = 3; // LinkedIn-style limit
    
    setOpenChats(prev => {
      // Check if chat is already open
      if (prev.find(chat => chat._id === conversation._id)) {
        return prev;
      }
      
      // If we're at the limit, remove the oldest chat
      if (prev.length >= maxOpenChats) {
        const newChats = prev.slice(1); // Remove first (oldest)
        return [...newChats, conversation];
      }
      
      return [...prev, conversation];
    });
    
    // Remove from minimized if it was minimized
    setMinimizedChats(prev => prev.filter(id => id !== conversation._id));
  }, []);

  const removeOpenChat = useCallback((conversationId) => {
    setOpenChats(prev => prev.filter(chat => chat._id !== conversationId));
    setMinimizedChats(prev => prev.filter(id => id !== conversationId));
  }, []);

  const minimizeChat = useCallback((conversationId) => {
    setMinimizedChats(prev => {
      if (!prev.includes(conversationId)) {
        return [...prev, conversationId];
      }
      return prev;
    });
  }, []);

  const maximizeChat = useCallback((conversationId) => {
    setMinimizedChats(prev => prev.filter(id => id !== conversationId));
  }, []);

  // Mark messages as read using centralized socketService
  const markAsRead = useCallback((threadId, messageCount = 1) => {
    if (socketService.isSocketConnected()) {
      socketService.emitEvent('mark-messages-read', { threadId, messageCount });
    }
  }, []);

  const contextValue = {
    // Unread message state
    unreadCount,
    setUnreadCount,

    // Floating chat widget state
    isFloatingChatOpen,
    setIsFloatingChatOpen,

    // Open chat windows state
    openChats,
    addOpenChat,
    removeOpenChat,
    minimizedChats,
    minimizeChat,
    maximizeChat,

    // Recent conversations
    recentConversations,
    setRecentConversations,
    fetchRecentConversations,

    // Utility functions
    fetchUnreadCount,
    requestNotificationPermission,
    markAsRead,
  };

  return (
    <GlobalMessageContext.Provider value={contextValue}>
      {children}
    </GlobalMessageContext.Provider>
  );
}; 